import React, { FC, useEffect, useRef, useState } from 'react';
import { AddUniversal, TrashCanOutlined } from '@duxton-web/icons';
import { Button, Form, Tooltip } from '@duxton-web/ui';
import scribe from '@grab-x/scribe-web-sdk';
import { useLocalStorage, usePrevious } from 'react-use';
import styled from 'styled-components';

import { ExperimentFormMode, ExperimentFormModeType } from '../..';
import { newCreationFlowEvents } from '../../../../common/Scribe/EventsList';
import SectionCollapse from '../../common/SectionCollapse';
import TreatmentCard from '../../common/TreatmentCard';
import {
  useTreatments,
  useVariables,
} from '../../contexts/ExperimentFormInfoProvider';
import {
  BALANCED_SHUFFLE_TIME_SLICE_MAX_TREATMENTS,
  CustomTimeSliceStrategy,
  ExperimentAssignmentStrategy,
  ExpFormField,
  SELECTED_EXPERIMENT_LS_KEY,
  TreatmentDefaultColors,
  TreatmentIds,
  VariableType,
} from '../../helper/consts';
import AddVariableModal from '../../Modals/AddVariableModal';
import DeleteVariableModal from '../../Modals/DeleteVariableModal';
import AssignmentStrategy from './AssignmentStrategy';
import { REQUESTED } from '@/constants';
import { Experiment } from '@/lib/App';

interface TreatmentsProps {
  mode: ExperimentFormModeType;
}

const Treatment: FC<TreatmentsProps> = ({ mode }) => {
  const isViewMode = mode === ExperimentFormMode.VIEW;
  const form = Form.useFormInstance();
  const [selectedExperiment] = useLocalStorage<Experiment>(
    SELECTED_EXPERIMENT_LS_KEY,
  );
  const isRequestedEdit =
    mode === ExperimentFormMode.EDIT && selectedExperiment?.state === REQUESTED;
  const currentDomain = Form.useWatch(ExpFormField.DOMAIN, {
    form,
    preserve: true,
  });

  const treatmentCarouselRef = useRef<HTMLDivElement>(null);

  const [treatments, setTreatments] = useTreatments();
  const prevTreatment = usePrevious(treatments);

  const [variables, setVariables] = useVariables();

  const [showDeleteVariableModal, setShowDeleteVariableModal] = useState({
    show: false,
    variableNameToDelete: null,
  });

  const [showAddVariableModal, setShowAddVariableModal] = useState(false);

  const currentStrategyTreatmentName = Form.useWatch(
    ExpFormField.STRATEGY.NAME,
    {
      form,
      preserve: true,
    },
  );

  const currentCustomTimeSliceStrategyName = Form.useWatch(
    ExpFormField.STRATEGY.CUSTOM_TIME_SLICE_STRATEGY_NAME,
    {
      form,
      preserve: true,
    },
  );

  const isUsingBalancedShuffleTimeSliceStrategy =
    currentStrategyTreatmentName ===
      ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE &&
    currentCustomTimeSliceStrategyName ===
      CustomTimeSliceStrategy.BALANCED_SHUFFLE;

  useEffect(() => {
    if (prevTreatment?.length < treatments.length) {
      treatmentCarouselRef.current?.scrollTo({
        left: treatmentCarouselRef.current.scrollWidth,
        behavior: 'smooth',
      });
    }
  }, [treatments.length, prevTreatment]);

  const handleAddTreatment = () => {
    if (treatments.length >= TreatmentIds.length) return;
    scribe.sendEvent(newCreationFlowEvents.clickAddTreatment.name, {
      experiment_name: form.getFieldValue(ExpFormField.NAME),
      STATE_NAME: newCreationFlowEvents.clickAddTreatment.state,
    });
    const newVariableId = TreatmentIds.find(
      (item) => !treatments.find((variable) => variable.id === item),
    );
    setTreatments([
      ...treatments,
      {
        id: newVariableId,
        name: `Treatment ${newVariableId}`,
        color: TreatmentDefaultColors[newVariableId],
      },
    ]);
  };

  const handleRemoveTreatment = (treatmentId) => {
    setTreatments(treatments.filter((item) => item.id !== treatmentId));
  };

  const handleEditTreatment = (treatmentId, newName, newDescription) => {
    setTreatments(
      treatments.map((item) =>
        item.id === treatmentId
          ? {
              ...item,
              name: newName,
              description: newDescription,
            }
          : item,
      ),
    );
  };

  const handleAddVariable = (variable) => {
    if (variables.length === 0) {
      form.setFieldValue(ExpFormField.DOMAIN, variable.domain);
    }
    setVariables([...variables, variable]);
    form.setFieldValue(ExpFormField.VARIABLES.DETAIL(variable.name), {});
  };

  const handleDeleteVariable = (variableName) => {
    setVariables(variables.filter((item) => item.name !== variableName));
    const currentFormVariables = form.getFieldValue(
      ExpFormField.VARIABLES.ROOT,
    );
    delete currentFormVariables[variableName];
    form.setFieldValue(ExpFormField.VARIABLES.ROOT, {
      ...currentFormVariables,
    });
  };

  const disableAddTreatment =
    treatments.length >= TreatmentIds.length ||
    (isUsingBalancedShuffleTimeSliceStrategy &&
      treatments.length >= BALANCED_SHUFFLE_TIME_SLICE_MAX_TREATMENTS);

  const getDisabledAddTreatmentTooltip = () => {
    if (treatments.length >= TreatmentIds.length) {
      return 'Maximum number of treatment reached';
    }
    if (isUsingBalancedShuffleTimeSliceStrategy) {
      return `Balanced shuffle strategy can't have more than ${BALANCED_SHUFFLE_TIME_SLICE_MAX_TREATMENTS} treatments`;
    }
    return '';
  };

  return (
    <>
      <SectionCollapse title="Treatments" defaultOpen>
        <TreatmentWrapper>
          <TreatmentCarousel ref={treatmentCarouselRef}>
            {treatments.map((item) => (
              <TreatmentCard
                disabled={isViewMode}
                deletable={
                  item.id !== 'Control' && item.id !== 'A' && !isViewMode
                }
                editable={!isViewMode}
                color={item.color}
                treatmentName={item.name}
                treatmentId={item.id}
                description={item.description}
                onDelete={() => handleRemoveTreatment(item.id)}
                onEdit={(newName, newDescription) =>
                  handleEditTreatment(item.id, newName, newDescription)
                }
                empty={variables.length === 0}
                key={item.id}
              />
            ))}
          </TreatmentCarousel>
          {!isViewMode && (
            <TreatmentControlContainer>
              {disableAddTreatment ? (
                <Tooltip
                  title={getDisabledAddTreatmentTooltip()}
                  placement="left"
                >
                  <span>
                    <AddTreatmentButton onClick={() => {}} disabled>
                      <AddUniversal />
                    </AddTreatmentButton>
                  </span>
                </Tooltip>
              ) : (
                <AddTreatmentButton onClick={handleAddTreatment}>
                  <AddUniversal />
                </AddTreatmentButton>
              )}
              {variables.map((item) => {
                const extraStyle =
                  item.type === VariableType.JSON
                    ? { marginBottom: '112px' }
                    : {};
                return (
                  <DeleteVariableButton
                    key={item.name}
                    disabled={isRequestedEdit}
                    aria-disabled={isRequestedEdit}
                    onClick={
                      isRequestedEdit
                        ? undefined
                        : () => {
                            setShowDeleteVariableModal({
                              show: true,
                              variableNameToDelete: item.name,
                            });
                          }
                    }
                    style={extraStyle}
                  >
                    <TrashCanOutlined />
                  </DeleteVariableButton>
                );
              })}
            </TreatmentControlContainer>
          )}
        </TreatmentWrapper>
        {!isViewMode && (
          <AddVariableButton
            onClick={(e) => {
              e.preventDefault();
              if (!isRequestedEdit) {
                scribe.sendEvent(newCreationFlowEvents.clickAddVariable.name, {
                  experiment_name: form.getFieldValue(ExpFormField.NAME),
                  STATE_NAME: newCreationFlowEvents.clickAddVariable.state,
                });
                setShowAddVariableModal(true);
              }
            }}
            type="secondary"
            disabled={isRequestedEdit}
            style={
              currentDomain && { background: '#00B1631A', color: '#005339' }
            }
          >
            Add variable
          </AddVariableButton>
        )}
        <AssignmentStrategy isViewMode={isViewMode} />
      </SectionCollapse>
      {showDeleteVariableModal.show && (
        <DeleteVariableModal
          variableName={showDeleteVariableModal.variableNameToDelete}
          onCancel={() => {
            setShowDeleteVariableModal({
              show: false,
              variableNameToDelete: null,
            });
          }}
          onOk={() => {
            handleDeleteVariable(showDeleteVariableModal.variableNameToDelete);
            setShowDeleteVariableModal({
              show: false,
              variableNameToDelete: null,
            });
          }}
        />
      )}
      {showAddVariableModal && (
        <AddVariableModal
          onCancel={() => {
            setShowAddVariableModal(false);
          }}
          onOk={(variable) => {
            handleAddVariable(variable);
            setShowAddVariableModal(false);
          }}
          mode={mode}
        />
      )}
    </>
  );
};

export const TreatmentWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const TreatmentCarousel = styled.div`
  display: flex;
  gap: 16px;
  overflow: auto;
  padding-bottom: 16px;
  padding-right: 4px;
  width: 100%;
`;

export const TreatmentControlContainer = styled.div`
  box-shadow: -8px 0 8px -8px rgb(0 0 0 / 25%);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  margin-bottom: 16px;
  padding-bottom: 32px;
  padding-left: 16px;
  z-index: 2;
`;

export const AddTreatmentButton = styled.div<{ disabled?: boolean }>`
  background-color: ${({ disabled }) => (disabled ? '#0000000a' : '#00B1631A')};
  color: ${({ disabled }) => (disabled ? '#707070' : '#00B163')};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  border-radius: 8px;
  display: flex;
  align-items: center;
  height: 40px;
  justify-content: center;
  margin-bottom: 18px;
  margin-top: 25px;
  width: 40px;
`;

export const DeleteVariableButton = styled.div<{ disabled?: boolean }>`
  align-items: center;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  background-color: ${({ disabled }) =>
    disabled ? '#0000000a' : 'transparent'};
  color: ${({ disabled }) => (disabled ? '#707070' : 'inherit')};
  height: 40px;
  justify-content: center;
  margin-top: 51px;
  width: 40px;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: auto;
`;

export const AddVariableButton = styled(Button)`
  border-radius: 8px;
  font-size: 14px;
  width: 100%;
  justify-content: center;
`;

export default Treatment;
